import {
  Typography,
  Stack,
  Grid,
  Box,
  IconButton,
  Tabs,
  Tab,
  Snackbar,
  useTheme,
  Link as MuiLink,
  List,
  ListItem,
} from '@mui/material';
import {StyledTooltip} from 'components/StyledTooltip/StyledTooltip';

import {Check, Info} from '@mui/icons-material';
import {DocumentCategories, IProcess, ProcessTabs} from 'app/types/Shared';

import * as React from 'react';

import {useDispatch, useSelector} from 'react-redux';
import {selectAllProcesses, selectProcessesWithMultipleModels} from 'app/store/operatingModel/operatingModelSlice';
import {useFeatureFlag} from 'configcat-react';
import {setProcessModelSelections, selectProcessModelSelections} from 'app/store/appState/appStateSlice';
import {PinFilled, Pin, Link, Launch} from '@carbon/icons-react';
import config from 'config';

type PSProps = {
  process: IProcess;
  pinned: boolean;
  children?: React.ReactNode;
  setPinCallback?: (add: boolean) => void;
  storybook?: boolean;
  onSearch?: (query: string, filter?: DocumentCategories) => Promise<void>;
  veiwAllCallback?: () => Promise<void>;
  changeTabs?: (tabIndex: number, tab: ProcessTabs) => void;
  navigateToProcess?: (arg0: string) => void;
  currentTab: number;
};

const ProcessDetailsPage = ({
  process,
  pinned,
  children,
  setPinCallback,
  storybook = false,
  changeTabs,
  currentTab,
  navigateToProcess,
}: PSProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {name} = process;
  const allProcesses = useSelector(selectAllProcesses);
  const processWithMultipleModels = useSelector(selectProcessesWithMultipleModels);
  const processModelSelections = useSelector(selectProcessModelSelections);
  const [pin, setPin] = React.useState(pinned);

  const [selectedOrg, setSelectedOrg] = React.useState<string | undefined>(process.operating_models_list);
  const [clipBoard, setClipBoard] = React.useState(false);
  const [value, setValue] = React.useState(0);
  const {value: isChangeNotificationsEnabled} = useFeatureFlag('feature_change_notifications', false);

  React.useEffect(() => {
    setPin(pinned);
  }, [pinned]);

  const handleClick = () => {
    if (pin) {
      setPin(false);
      if (setPinCallback) {
        setPinCallback(false);
      }
    } else {
      setPin(true);
      if (setPinCallback) {
        setPinCallback(true);
      }
    }
  };

  const Copy = (link: string) => {
    navigator.clipboard
      .writeText(link)
      .then(() => {
        console.log('Link copied to clipboard');
        setClipBoard(true);
      })
      .catch((error) => {
        console.error('Copy to clipboard failed:', error);
      });
  };

  function a11yProps(name: string, id: number) {
    return {
      id: `tab-${name}.${id}`,
    };
  }

  const convertToProcessTab = (tab: string) => {
    switch (tab) {
      case ProcessTabs.Overview:
        return ProcessTabs.Overview;
      case ProcessTabs.Map:
        return ProcessTabs.Map;
      case ProcessTabs.RACI:
        return ProcessTabs.RACI;
      case ProcessTabs.Documents:
        return ProcessTabs.Documents;
      // case ProcessTabs.Learning:
      //   return ProcessTabs.Learning;
      default:
        return null;
    }
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (event) {
      if (changeTabs) {
        const newSTR = event.currentTarget.id.replace('tab-', '');
        const idx = newSTR.indexOf('.');
        const processTab = convertToProcessTab(newSTR.substring(0, idx));

        processTab && changeTabs(newValue, processTab);
      }
      storybook && setValue(newValue);
    }
  };

  const handleOrgChange = (model?: string) => {
    if (model) {
      setSelectedOrg(model);
      const newProcess = allProcesses?.find((x) => x.name === process.name && x.operating_models_list === model);
      if (newProcess) {
        if (!processModelSelections?.includes({process_name: newProcess.name, model: model})) {
          dispatch(setProcessModelSelections({process_name: newProcess.name, model: model}));
        }
        navigateToProcess && navigateToProcess(newProcess.process_id);
      }
    }
  };

  const getAllModels = () => {
    return processWithMultipleModels
      ?.filter((p) => p.name === process.name)
      .map((m) => m.model)
      .sort((a, b) => {
        if (a && b) {
          if (a > b) {
            return 1;
          } else {
            return -1;
          }
        } else return 0;
      });
  };

  return (
    <>
      <Stack direction='column'>
        <Grid container direction='column' py={2} px={6}>
          <Grid item display='flex'>
            <Box flexGrow={1} display='flex' alignItems='center' columnGap='10px'>
              <Typography variant='h1'>{name}</Typography>
              <IconButton disableRipple onClick={() => Copy(window.location.href)} sx={{p: 0}}>
                <Box sx={{color: theme.palette.grey[700], '&:hover': {color: theme.palette.secondary.main}}}>
                  <Link color='inherit' height='20px' width='20px' />
                </Box>
              </IconButton>
              <IconButton onClick={() => handleClick()} disableRipple sx={{p: 0}}>
                {pin ? (
                  <Box>
                    <PinFilled fill={theme.palette.primary.main} height='20px' width='20px' />
                  </Box>
                ) : (
                  <Box sx={{color: theme.palette.grey[700], '&:hover': {color: theme.palette.secondary.main}}}>
                    <Pin color='inherit' height='20px' width='20px' />
                  </Box>
                )}
              </IconButton>
              {processWithMultipleModels?.some((p) => p.process_id === process.process_id) && (
                <Stack ml={2} direction='row' spacing='5px' alignItems='center'>
                  {getAllModels()?.map((item) => (
                    <Box
                      key={item}
                      onClick={() => handleOrgChange(item)}
                      sx={{
                        p: '10px',
                        maxHeight: '26px',
                        minHeight: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        border:
                          selectedOrg === item
                            ? `1.5px solid ${theme.palette.primary.main}`
                            : `1px solid ${theme.palette.grey[500]}`,
                        '&:hover': {
                          bgcolor: theme.palette.grey[100],
                          cursor: 'pointer',
                          border: `1px solid ${theme.palette.grey[500]}`,
                        },
                      }}
                    >
                      <Stack direction='row' spacing='10px'>
                        {selectedOrg === item && <Check fontSize='small' sx={{color: theme.palette.primary.main}} />}
                        <Typography variant='h5' color={selectedOrg === item ? theme.palette.primary.main : 'inherit'}>
                          {item}
                        </Typography>
                      </Stack>
                    </Box>
                  ))}
                  <StyledTooltip
                    title={
                      <Box sx={{pl: 0, pr: '10px', pb: '5px', pt: '5px'}}>
                        <Stack direction='column' spacing={'10px'}>
                          <Typography variant='h6'>
                            This process can be performed with multiple resourcing models:
                          </Typography>
                          <Typography variant='h6'>
                            <b>Insourced</b> includes Takeda resources only.
                          </Typography>
                          <Typography variant='h6'>
                            <b>Hybrid</b> includes Takeda and CRO resources.
                          </Typography>
                          <Typography variant='h6'>{`If you’re unsure which model to choose, talk with your study team.`}</Typography>
                        </Stack>
                      </Box>
                    }
                  >
                    <Info fontSize='small' sx={{color: theme.palette.grey[600]}} />
                  </StyledTooltip>
                </Stack>
              )}
            </Box>
          </Grid>
          {/* <Box sx={{display: 'flex', flexDirection: 'row'}}>
            <Box flexGrow={1}>
              <Grid item container direction='row'>
                <Grid item sm={6} md={5} lg={4}>
                  <Typography variant='subtitle1'>{desc}</Typography>
                </Grid>
              </Grid>
            </Box>
          </Box> */}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Tabs
              value={storybook ? value : currentTab}
              onChange={handleChange}
              textColor='secondary'
              sx={{px: 6, minWidth: 0, minHeight: 0}}
              TabIndicatorProps={{
                style: {
                  display: 'none',
                },
              }}
            >
              {Object.values(ProcessTabs)
                .filter((tab) => {
                  if (tab === ProcessTabs.Map && process.diagram_link === null) return false;
                  if (isChangeNotificationsEnabled) {
                    return true;
                  } else return tab !== ProcessTabs.Overview;
                })
                .map((item, index) => (
                  <Tab
                    key={item + index}
                    label={
                      <Box
                        px='20px'
                        height='39px'
                        display='flex'
                        alignItems='center'
                        bgcolor={currentTab === index ? 'primary.main' : theme.palette.grey[100]}
                        border={
                          currentTab === index
                            ? `1px solid ${theme.palette.primary.main}`
                            : `1px solid ${theme.palette.grey[500]}`
                        }
                        borderBottom={'none'}
                        color={currentTab === index ? 'white' : 'grey.800'}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'white',
                            border: `1px solid ${theme.palette.primary.main}`,
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        <Typography variant='h5'>{item}</Typography>
                      </Box>
                    }
                    disableRipple
                    {...a11yProps(item, index)}
                    sx={{
                      p: 0,
                      mr: '3px',
                      minWidth: 0,
                      minHeight: 0,
                    }}
                  />
                ))}
            </Tabs>
          </Grid>
          <Grid
            sx={{
              position: 'relative',
              marginLeft: 'auto',
              minWidth: '250px',
              marginRight: '40px',
            }}
            item
            xs={1}
          >
            <StyledTooltip
              title={
                <Box sx={{pl: 0, pr: '10px', pb: '5px', pt: '5px'}}>
                  Send an email regarding this process:
                  <List sx={{listStyleType: 'disc'}}>
                    <ListItem sx={{display: 'list-item', marginLeft: '20px', paddingLeft: '1px'}}>
                      Report an error or omission
                    </ListItem>
                    <ListItem sx={{display: 'list-item', marginLeft: '20px', paddingLeft: '1px'}}>
                      Suggest an improvement
                    </ListItem>
                    <ListItem sx={{display: 'list-item', marginLeft: '20px', paddingLeft: '1px'}}>
                      Request access to a document
                    </ListItem>
                  </List>
                </Box>
              }
            >
              <Info fontSize='small' sx={{color: theme.palette.grey[600]}} />
            </StyledTooltip>
            &nbsp;
            <MuiLink
              style={{top: '-6px', position: 'relative', textDecoration: 'none'}}
              href={`mailto:${config.bpoGloblMailbox}?subject=${process.name}`}
            >
              Contact the process team
            </MuiLink>
            &nbsp;
            <Launch style={{position: 'relative'}} color={'inherit'} height='20px' width='20px' />
          </Grid>
        </Grid>
        {children}
      </Stack>
      <Snackbar
        open={clipBoard}
        autoHideDuration={8000}
        onClose={() => setClipBoard(false)}
        message={
          <Stack direction='row' spacing={1} alignItems='center' justifyContent='flex-start'>
            <Link color='white' />
            <Typography>Link Copied to Clipboard</Typography>
          </Stack>
        }
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
      />
    </>
  );
};

export default ProcessDetailsPage;
/* Hover Update Text */
